import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './random-color.component.module.scss';
import { genStyles } from '../converter-layout/generator-layout';

type Format = 'hex' | 'rgb' | 'hsl' | 'oklch';

interface ColorSample { hex: string; format: string; }

interface State {
    format: Format;
    count: number;
    constrainHue: boolean;
    hueMin: number;
    hueMax: number;
    satMin: number;
    satMax: number;
    lightMin: number;
    lightMax: number;
    output: string;
    colors: ColorSample[];
    copied: boolean;
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
    s /= 100; l /= 100;
    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
}

export default class RandomColorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        format: 'hex',
        count: 12,
        constrainHue: false,
        hueMin: 0, hueMax: 360,
        satMin: 30, satMax: 90,
        lightMin: 30, lightMax: 70,
        output: '',
        colors: [],
        copied: false,
    };

    componentDidMount() {
        // Defer first random pass to componentDidMount so SSR and first
        // client render both see an empty `colors` array — eliminates the
        // hydration mismatch from calling Math.random() during render.
        this.generate();
    }

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private computeColors(): ColorSample[] {
        const out: ColorSample[] = [];
        for (let i = 0; i < this.state.count; i++) {
            const h = this.state.hueMin + Math.random() * (this.state.hueMax - this.state.hueMin);
            const s = this.state.satMin + Math.random() * (this.state.satMax - this.state.satMin);
            const l = this.state.lightMin + Math.random() * (this.state.lightMax - this.state.lightMin);
            const [r, g, b] = hslToRgb(h, s, l);
            const hex = '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
            let formatted = '';
            if (this.state.format === 'hex') formatted = hex;
            else if (this.state.format === 'rgb') formatted = `rgb(${r}, ${g}, ${b})`;
            else if (this.state.format === 'hsl') formatted = `hsl(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%)`;
            else formatted = `oklch(${(l / 100).toFixed(3)} ${(s / 100 * 0.4).toFixed(3)} ${Math.round(h)})`;
            out.push({ hex, format: formatted });
        }
        return out;
    }

    generate = () => {
        const colors = this.computeColors();
        this.setState({ colors, output: colors.map(c => c.format).join('\n'), copied: false });
    };

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { format, count, output, copied, hueMin, hueMax, satMin, satMax, lightMin, lightMax, colors } = this.state;
        return (
            <div className="row">
                <div className="col-12 col-md-5">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>
                            Format
                            <select className={styles.input} value={format}
                                onChange={e => this.setState({ format: e.target.value as Format })}>
                                <option value="hex">HEX</option>
                                <option value="rgb">RGB</option>
                                <option value="hsl">HSL</option>
                                <option value="oklch">OKLCH</option>
                            </select>
                        </label>
                        <label className={sharedStyles.label}>
                            Count
                            <input className={styles.input} type="number" inputMode="decimal" min={1} max={500} value={count}
                                onChange={e => this.setState({ count: Math.max(1, Math.min(500, +e.target.value || 12)) })} />
                        </label>
                        <div className={styles.rangeRow}>
                            <span>Hue {hueMin}–{hueMax}°</span>
                            <input type="range" min={0} max={360} value={hueMin}
                                onChange={e => this.setState({ hueMin: +e.target.value })} />
                            <input type="range" min={0} max={360} value={hueMax}
                                onChange={e => this.setState({ hueMax: +e.target.value })} />
                        </div>
                        <div className={styles.rangeRow}>
                            <span>Saturation {satMin}–{satMax}%</span>
                            <input type="range" min={0} max={100} value={satMin}
                                onChange={e => this.setState({ satMin: +e.target.value })} />
                            <input type="range" min={0} max={100} value={satMax}
                                onChange={e => this.setState({ satMax: +e.target.value })} />
                        </div>
                        <div className={styles.rangeRow}>
                            <span>Lightness {lightMin}–{lightMax}%</span>
                            <input type="range" min={0} max={100} value={lightMin}
                                onChange={e => this.setState({ lightMin: +e.target.value })} />
                            <input type="range" min={0} max={100} value={lightMax}
                                onChange={e => this.setState({ lightMax: +e.target.value })} />
                        </div>
                        <button className={sharedStyles.buttonPrimary} onClick={this.generate}>Generate</button>
                    </div>
                </div>
                <div className="col-12 col-md-7">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>Swatches</label>
                        <div className={styles.swatchGrid}>
                            {colors.map((c, i) => (
                                <button key={i} type="button" className={styles.swatch} style={{ background: c.hex }}
                                    title={`Click to copy: ${c.format}`}
                                    onClick={() => navigator.clipboard?.writeText(c.format)}>
                                    <span className={styles.swatchLabel}>{c.format}</span>
                                </button>
                            ))}
                        </div>
                        <label className={sharedStyles.label} style={{ marginTop: '1rem' }}>Output</label>
                        <textarea className={`${sharedStyles.textarea} ${styles.output}`}
                            value={output} readOnly rows={Math.min(count + 1, 12)} />
                        <button className={`${sharedStyles.copyButton} ${copied ? sharedStyles.copyButtonSuccess : ''}`}
                            onClick={this.copyOutput} disabled={!output}>
                            {copied ? '✓ Copied' : 'Copy all'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
